import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import Marquee from '../Marquee';
import SpacedHeading from '../SpacedHeading';
import { solutions } from '../../mock';
import { ArrowUpRight } from 'lucide-react';

export default function Solutions() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const [scrollRange, setScrollRange] = useState(0);

  // Measure the actual pixel width of the cards track vs viewport
  useEffect(() => {
    const measure = () => {
      if (trackRef.current) {
        const trackWidth = trackRef.current.scrollWidth;
        const viewportWidth = window.innerWidth;
        // How far we need to translate: track overflows by this many px
        setScrollRange(trackWidth - viewportWidth);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"]
  });

  // Transform: as page scrolls vertically through the 400vh container,
  // translate the cards track from 0px to -(trackWidth - viewportWidth)px
  const xRaw = useTransform(scrollYProgress, [0, 1], [0, -scrollRange]);
  // Smooth spring for buttery feel
  const x = useSpring(xRaw, { stiffness: 100, damping: 30, mass: 0.5 });

  return (
    <>
      <div className="bg-[var(--bg)] pt-20 md:pt-28">
        <Marquee
          items={['HABILIDADES', 'HABILIDADES', 'HABILIDADES', 'HABILIDADES']}
          speed={8}
          className="border-y border-[var(--border)]"
        />
      </div>

      <section id="solutions" className="relative bg-[var(--bg)] pb-0">
        {/* Título rola naturalmente e some com o scroll */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 w-full mt-20 md:mt-28 mb-20 md:mb-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <SpacedHeading className="text-4xl md:text-7xl lg:text-8xl">
              MINHAS HABILIDADES
            </SpacedHeading>
            <a
              href="https://api.whatsapp.com/send?1=pt_BR&phone=5535984641337"
              target="_blank"
              rel="noopener noreferrer"
              className="self-start group inline-flex items-center gap-2 text-xs tracking-[0.3em] border border-[var(--border)] rounded-full px-5 py-3 hover:bg-[var(--accent)] hover:border-[var(--accent)] hover:text-white transition-colors"
            >
              VAMOS CONVERSAR
              <ArrowUpRight size={14} strokeWidth={2} />
            </a>
          </div>
        </div>

        {/* Container de scroll horizontal com Sticky apenas para os cards */}
        <div ref={sectionRef} style={{ height: '400vh' }}>
          {/* Trava próximo ao topo, permitindo que os cards fiquem altos e longe da base */}
          <div className="sticky top-[110px] md:top-28 w-full overflow-hidden">
            <div className="flex items-center">
              <motion.div
                ref={trackRef}
                style={{ x }}
                className="flex gap-6 md:gap-10 pl-6 md:pl-10"
              >
                {solutions.map((s) => (
                  <SolutionCard key={s.num} sol={s} />
                ))}
                {/* Spacer no final */}
                <div className="shrink-0 w-[10vw]" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function SolutionCard({ sol }) {
  return (
    <div className="shrink-0 w-[85vw] md:w-[60vw] lg:w-[45vw] h-[60vh] md:h-[65vh] relative rounded-[var(--card-radius)] overflow-hidden border border-[var(--border)] group bg-[var(--card)]">
      <img
        src={sol.image}
        alt={sol.title}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-[1.2s] ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/90" />

      <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-12">
        <div className="flex justify-between items-start">
          <h3 className="font-display text-4xl md:text-6xl tracking-tight uppercase italic">{sol.title}</h3>
          <span className="font-display text-5xl md:text-7xl text-[var(--accent)] opacity-50">{sol.num}</span>
        </div>

        <ul className="space-y-3 mt-auto">
          {sol.items.map((it) => (
            <li
              key={it}
              className="text-sm md:text-base tracking-[0.2em] uppercase text-white/80 border-b border-[var(--border)] pb-2 flex justify-between items-center group/item"
            >
              {it}
              <ArrowUpRight size={14} className="opacity-0 group-hover/item:opacity-100 transition-opacity text-[var(--accent)]" />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
